/**
 * File: agentJobTargets.ts
 * Description: Shared constants for the agent-job tracker. Job-type strings
 *   mirror the backend `JOB_TYPE_*` set in
 *   `src/backend/src/domain/async_job.py`; target builders produce the
 *   per-page join key a component uses to locate "its" tracked job.
 */

/** Window event dispatched when a tracked job reaches a terminal state. */
export const AGENT_JOB_COMPLETED_EVENT = 'agentJobCompleted';

/** Job-type identifiers (kept in sync with the backend domain constants). */
export const AgentJobType = {
  CAPABILITY_MAPPING: 'capability_mapping',
  ADR_GENERATION: 'adr_generation',
  ADR_SUGGESTION: 'adr_suggestion',
  PEER_REVIEW: 'peer_review',
  PEER_REVIEW_FIX: 'peer_review_fix',
  SECTION_GENERATION: 'section_generation',
  SECTION_EDIT: 'section_edit',
  DOCUMENT_EXPORT: 'document_export',
  C4_DIAGRAM_GENERATION: 'c4_diagram_generation',
  C4_DIAGRAM_REFINEMENT: 'c4_diagram_refinement',
  TECH_DIAGRAM_GENERATION: 'tech_diagram_generation',
  TECH_DIAGRAM_REFINEMENT: 'tech_diagram_refinement',
  INTERFACE_DIAGRAM_GENERATION: 'interface_diagram_generation',
  INTERFACE_DIAGRAM_REFINEMENT: 'interface_diagram_refinement',
  ADO_BACKLOG_GENERATION: 'ado_backlog_generation',
  ADO_SEND_TO_CODIE: 'ado_send_to_codie',
} as const;

export type AgentJobTypeValue =
  (typeof AgentJobType)[keyof typeof AgentJobType];

/** Target for an ADR generation job (one per ADR definition). */
export const adrGenerationTarget = (adrId: string): string => `adr:${adrId}`;

/** Target for the project-wide ADR suggestion job. */
export const adrSuggestionTarget = (): string => 'adr:suggestions';

/** Target for a section generation/edit job (one per architecture section). */
export const sectionTarget = (section: string): string => `section:${section}`;

/**
 * Target for a diagram generate/refine job. `kind` distinguishes the diagram
 * family (c4/tech/interface) and `mode` distinguishes generate vs refine.
 */
export const diagramTarget = (
  kind: 'c4' | 'tech' | 'interface',
  mode: 'generate' | 'refine',
  view?: string,
): string => {
  const viewSuffix = view ? `:${view}` : '';
  return `${kind}:${mode}${viewSuffix}`;
};

/** Target for the capability-mapping job. */
export const capabilityMappingTarget = (): string => 'capability_mapping';
